import { createContext, useContext } from 'react';
import type {
  ElementCategory,
  EvidenceSpan,
  ItemEvidence,
  ItemVerb,
  SceneDetailView,
  SceneIndexRow,
  SceneItemView,
} from '@scoutaround/script-breakdown-shared';

import type { RequirementsRead } from '../scenes/SceneLocationRequirements.js';

/**
 * WHAT THE PANES OF THE SCENE BREAKDOWN SHARE: the open scene as the server answered it, the
 * item in view, and the verbs. The container (`ElementsWorkspace`) owns every query and every
 * write; the panes render what it gives them and call these, so a pane never re-derives a figure
 * and never writes behind the container's back.
 */
export interface SceneBreakdownValue {
  readonly projectId: string;
  readonly scriptVersionId: string;
  /** The open scene's index row: progress, coverage and counts as the index pass computed them. */
  readonly row: SceneIndexRow | null;
  readonly detail: SceneDetailView | null;
  readonly detailLoading: boolean;
  readonly detailError: string | null;
  readonly retryDetail: () => void;

  /** The item in view on the right (`?item=`), by its key. */
  readonly selectedItem: string | null;
  readonly selectItem: (key: string | null) => void;
  /** Put an item's place in the script in view and pulse it. */
  readonly showItem: (item: SceneItemView) => void;
  /** Put an evidence span (a takeaway's quote, a plan view's) in view and pulse it. */
  readonly showEvidence: (span: EvidenceSpan) => void;
  /** The same for evidence already resolved to ranges (a takeaway's). */
  readonly showRanges: (evidence: ItemEvidence) => void;

  readonly decide: (item: SceneItemView, verb: ItemVerb) => void;
  readonly rename: (item: SceneItemView, name: string) => void;
  readonly setCount: (item: SceneItemView, quantity: number | null) => void;
  readonly remove: (item: SceneItemView) => void;
  /**
   * Ask, then take an item off every scene of this draft whose sheet carries it — the server's
   * `onScenes` list, shown before anything is removed. Only for an item on more than one scene.
   */
  readonly removeEverywhere: (item: SceneItemView) => void;
  readonly add: (category: ElementCategory, name: string) => void;
  readonly markReviewed: () => void;
  readonly unmarkReviewed: () => void;
  /** A paid reading was started from a pane: the run is watched and its answer re-read when it ends. */
  readonly onReadingStarted: () => void;
  /**
   * Open the paid reading of the open scene alone (its estimate and approval first, as every paid
   * reading). Null while a reading runs or for a role that does not run AI; the reader decides on
   * the scene's coverage whether to offer it (`sceneRereadOffered`).
   */
  readonly readScene: (() => void) | null;

  /** A write is on its way: the verbs wait for it. */
  readonly busy: boolean;
  /** The last write's refusal, in words. */
  readonly error: string | null;
  readonly clearError: () => void;
  /** Why this person may not change the breakdown, or null when they may (a hint, never the gate). */
  readonly refusal: string | null;

  readonly planOn: boolean;
  readonly takeawaysOn: boolean;
  /** The location requirements read, for the Location row (read-only). */
  readonly requirements: RequirementsRead;
}

export const SceneBreakdownContext = createContext<SceneBreakdownValue | null>(null);

export function useSceneBreakdown(): SceneBreakdownValue {
  const value = useContext(SceneBreakdownContext);
  if (value === null) throw new Error('A scene breakdown pane rendered outside its workspace');

  return value;
}
